import React, {useState, useRef, useEffect} from "react";
import PropTypes from "prop-types";
import {DataGridItem} from "components/data-grid";
import {injectIntl, FormattedMessage} from "react-intl";
import {CardWithDelete} from "components/card";
import withStyles from "components/with-styles";
import styles from "./styles/livestream-form.css.js";
import {IconRadioGroup, IconRadioButton} from "components/icon-radio-buttons";
import StaticIconRadioButton from "../../../../../components/static-icon-radio-button/static-icon-radio-button";
import {
    dailymotionIcon,
    dailymotionIconBlack, dailymotionIconDisabled,
    tiiviiIcon,
    tiiviiIconBlack,
    tiiviiIconDisabled, twitchIcon,
    twitchIconBlack, twitchIconDisabled
} from "../../../../../config/paths";
import {
    EVENT_PAGE_PARAM_NEW, LIVESTREAM_TYPE_CUSTOM,
    LIVESTREAM_TYPE_TIIVII_OTT,
    SERVICES_METADATA,
    STREAM_STATUS_DELETED
} from "./livestream-form-constants";
import {useSelector, useDispatch} from "react-redux";
import { storeTiiviiRtmpConfigActionCreator } from "../../../../../actions/common-actions.js";
import {validateRtmpUrl} from "../../utils/validateRtmpUrl";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";

const getServiceFromUrl = livestream => {
    const emptyLiveStream = Object.values(livestream).every(value => {
        if (value === "") {
            return true;
        }
    });

    if (emptyLiveStream) {
        return LIVESTREAM_TYPE_CUSTOM;
    }

    const servicesHasRTMPURL = !!livestream.rtmpUrl ?
        Object.keys(SERVICES_METADATA).find(service => SERVICES_METADATA[service].url === livestream.rtmpUrl) :
        undefined;

    if (!!servicesHasRTMPURL) {
        return servicesHasRTMPURL
    }

    const isOTTDescription = livestream.description === SERVICES_METADATA[LIVESTREAM_TYPE_TIIVII_OTT].description;

    return isOTTDescription ? SERVICES_METADATA[LIVESTREAM_TYPE_TIIVII_OTT].description : SERVICES_METADATA.custom.name;
}

const isEditableRtmpUrl = service =>
    service === SERVICES_METADATA.custom.name || service === SERVICES_METADATA.youtube.name || service === SERVICES_METADATA.twitch.name;

const EventLiveStreamApiForm = ({onDelete, liveStream, handleChange, index, register, classes, intl, errors, setValue, watch}) => {
    const isFirst = useRef(true);

    const page = window.location.pathname.split("/")[2];

    const liveStreams = useSelector(state => state.commonState.liveStreams);
    const hasTiiviiOttConfig = useSelector(state => state.commonState.hasTiiviiOttConfig);

    const [service, setService] = useState(getServiceFromUrl(liveStream));
    const [editableRtmpUrl, setEditableRtmpUrl] = useState(isEditableRtmpUrl(service));
    const [canAddTiiviiOttStream, setCanAddTiiviiOttStream] = useState(null);
    const [livestreamKeyWarning, setLivestreamKeyWarning] = useState(false);

    const handleChangeRadioButton = id => {
        setService(id);
        setEditableRtmpUrl(isEditableRtmpUrl(id));
        if (SERVICES_METADATA.hasOwnProperty(id)) {
            if (!!SERVICES_METADATA[id].type) {
                handleChange(`liveStreams[${index}].type`, SERVICES_METADATA[id].type);
            }

            handleChange(`liveStreams[${index}].rtmpUrl`, SERVICES_METADATA[id].url);
            handleChange(`liveStreams[${index}].description`, SERVICES_METADATA[id].description);
        }
    };

    const checkIfCanAddTiiviiOttStream = array => {
        let condition;

        if (page !== EVENT_PAGE_PARAM_NEW) {
            condition = array.filter(stream => stream.description === LIVESTREAM_TYPE_TIIVII_OTT && stream.componentStatus !== STREAM_STATUS_DELETED).length === 0;
        } else {
            condition = (array.length > 1 && array.filter(stream => stream.type === LIVESTREAM_TYPE_TIIVII_OTT).length === 0) || array.length <= 1;
        }

        const alreadyTiiviiOtt = array.find(stream => stream.type === LIVESTREAM_TYPE_TIIVII_OTT || stream.description === LIVESTREAM_TYPE_TIIVII_OTT)
        const i = array.indexOf(alreadyTiiviiOtt)

        if (index === i) {
            condition = true
        }

        return condition;
    }

    useEffect(() => {
            if (isFirst.current) {
                handleChange(`liveStreams[${index}].rtmpUrl`, liveStream.rtmpUrl || SERVICES_METADATA[service].url);
                handleChange(`liveStreams[${index}].description`, liveStream.description || SERVICES_METADATA[service].description);

                if (service === LIVESTREAM_TYPE_TIIVII_OTT) {
                    handleChange(`liveStreams[${index}].type`, SERVICES_METADATA[service].type);
                    handleChange(`liveStreams[${index}].description`, SERVICES_METADATA[service].description);
                } else if (!liveStream.type && SERVICES_METADATA[service]?.type) {
                    handleChange(`liveStreams[${index}].type`, SERVICES_METADATA[service].type);
                }

                isFirst.current = false;
            }
        }, [handleChange, liveStream.description, liveStream.rtmpUrl, service]);

        useEffect(() => {
            setCanAddTiiviiOttStream(checkIfCanAddTiiviiOttStream(liveStreams))
        }, [liveStreams, liveStream])

        const dispatch = useDispatch();
        const isEmptyRtmpUrl = !(liveStream?.rtmpUrl !== '' && validateRtmpUrl(liveStream?.rtmpUrl));

        useEffect(() => {
            dispatch(storeTiiviiRtmpConfigActionCreator(isEmptyRtmpUrl))
        }, [isEmptyRtmpUrl])

        useEffect(() => {
            setService(getServiceFromUrl(liveStream));
        }, [liveStream]);

    const validateLivestreamKey = (value) => {
        const trimmedKey = value.trim();

        if (!trimmedKey) {
            setLivestreamKeyWarning(false);
            return;
        }

        const regex = /^[a-zA-Z0-9_-]+$/;
        setLivestreamKeyWarning(!regex.test(trimmedKey));
    }

    const handleChangeLivestreamKey = (e) => {
        validateLivestreamKey(e.target.value);
        setValue(`liveStreams[${index}].key`, e.target.value);
    }

    return (
        <CardWithDelete onDelete={onDelete}>
            <DataGridItem big>
                <div>
                    <IconRadioGroup value={service} onChange={handleChangeRadioButton}>
                        <IconRadioButton
                            id={SERVICES_METADATA.youtube.name}
                            label={intl.formatMessage({id: "app.event.livestream.type.youtube"})}
                            iconName="youtube"
                            disabled={page !== EVENT_PAGE_PARAM_NEW && !liveStream.isNew}
                        />
                        <IconRadioButton
                            id={SERVICES_METADATA.facebook.name}
                            label={intl.formatMessage({id: "app.event.livestream.type.facebookLive"})}
                            iconName="facebook"
                            disabled={page !== EVENT_PAGE_PARAM_NEW && !liveStream.isNew}
                        />
                        <IconRadioButton
                            id={SERVICES_METADATA.vimeo.name}
                            label={intl.formatMessage({id: "app.event.livestream.type.vimeo"})}
                            iconName="video-library"
                            disabled={page !== EVENT_PAGE_PARAM_NEW && !liveStream.isNew}
                        />
                        <StaticIconRadioButton
                            id={SERVICES_METADATA[LIVESTREAM_TYPE_TIIVII_OTT].name}
                            label={intl.formatMessage({id: "app.event.livestream.type.tiivii.ott"})}
                            srcSelected={tiiviiIcon} srcDeselected={tiiviiIconBlack} srcDisabled={tiiviiIconDisabled}
                            disabled={!hasTiiviiOttConfig || !canAddTiiviiOttStream || page !== EVENT_PAGE_PARAM_NEW && !liveStream.isNew}
                        />
                        <StaticIconRadioButton
                            id={SERVICES_METADATA.twitch.name}
                            label={intl.formatMessage({id: "app.event.livestream.type.twitch"})}
                            srcSelected={twitchIcon}
                            srcDeselected={twitchIconBlack}
                            srcDisabled={twitchIconDisabled}
                            disabled={page !== EVENT_PAGE_PARAM_NEW && !liveStream.isNew}
                        />
                        <StaticIconRadioButton
                            id={SERVICES_METADATA.dailymotion.name}
                            label={intl.formatMessage({id: "app.event.livestream.type.dailymotion"})}
                            srcSelected={dailymotionIcon}
                            srcDeselected={dailymotionIconBlack}
                            srcDisabled={dailymotionIconDisabled}
                            disabled={page !== EVENT_PAGE_PARAM_NEW && !liveStream.isNew}
                        />
                        <IconRadioButton
                            id={SERVICES_METADATA.custom.name}
                            label={intl.formatMessage({id: "app.event.livestream.type.custom"})}
                            iconName="build"
                            disabled={page !== EVENT_PAGE_PARAM_NEW && !liveStream.isNew}
                        />
                    </IconRadioGroup>
                </div>
            </DataGridItem>
            {service === LIVESTREAM_TYPE_TIIVII_OTT ?
                <div>
                    <DataGridItem big>
                        {hasTiiviiOttConfig &&
                            <span>{intl.formatMessage({id: "app.event.livestream.tiivii.ott.message"})}</span>}
                    </DataGridItem>
                </div> :
                <>
                    <DataGridItem big>
                        <TextField
                            {...register(`liveStreams[${index}].rtmpUrl`)}
                            type="text"
                            placeholder={intl.formatMessage({id: "event.liveStream.rtmpUrl"})}
                            label={intl.formatMessage({id: "event.liveStream.rtmpUrl"})}
                            disabled={!editableRtmpUrl}
                            error={!!errors?.rtmpUrl}
                            helperText={errors?.rtmpUrl?.message}
                            className={classes.fullWidth}
                            InputLabelProps={{ shrink: true }}
                        />
                    </DataGridItem>
                    <DataGridItem big>
                        <TextField
                            {...register(`liveStreams[${index}].key`)}
                            type="text"
                            placeholder={intl.formatMessage({id: "event.liveStream.key"})}
                            label={intl.formatMessage({id: "event.liveStream.key"})}
                            error={!!errors?.key}
                            helperText={errors?.key?.message}
                            className={classes.fullWidth}
                            onChange={handleChangeLivestreamKey}
                        />
                        {livestreamKeyWarning && (
                            <Typography variant="caption" color="textSecondary">{intl.formatMessage({id: 'event.key.novalid'})}</Typography>
                        )}
                    </DataGridItem>
                    <DataGridItem big>
                        <TextField
                            {...register(`liveStreams[${index}].description`)}
                            type="text"
                            placeholder={intl.formatMessage({id: "common.description"})}
                            label={intl.formatMessage({id: "common.description"})}
                            error={!!errors?.description}
                            helperText={errors?.description?.message}
                            className={classes.fullWidth}
                        />
                    </DataGridItem>
                    <DataGridItem big>
                        <TextField
                            {...register(`liveStreams[${index}].publishingUrl`)}
                            type="text"
                            placeholder={intl.formatMessage({id: "event.liveStream.publishingUrl"})}
                            label={intl.formatMessage({id: "event.liveStream.publishingUrl"})}
                            error={!!errors?.publishingUrl}
                            helperText={errors?.publishingUrl?.message}
                            className={classes.fullWidth}
                        />
                    </DataGridItem>
                    <DataGridItem big>
                        {(liveStream.rtmpUrl || liveStream.key) && (
                            <div className={classes.finalUrl}>
                                <FormattedMessage
                                    id="event.livestream.finalUrl"
                                    values={{
                                        rtmpUrl: liveStream.rtmpUrl.trim().endsWith("/") ? liveStream.rtmpUrl : liveStream.rtmpUrl + "/",
                                        key: liveStream.key,
                                    }}
                                />
                            </div>
                        )}
                    </DataGridItem>
                </>}
        </CardWithDelete>
    );
};

EventLiveStreamApiForm.propTypes = {
    /**
     * Indica si se muestra en una tarjeta
     */
    showInCard: PropTypes.bool,
    /**
     * Función que se invoca al eliminar el contacto del formulario
     */
    onDelete: PropTypes.func,
    /**
     ** Función que se invoca al editar un input
     **/
    onChange: PropTypes.func,
    /**
     ** Livestream to render
     **/
    element: PropTypes.object,
    /**
     * Índice del elemento en la colección
     */
    index: PropTypes.number,
    /**
     * Nombre de la colección
     */
    collectionName: PropTypes.string,
};

export default withStyles(styles)(injectIntl(EventLiveStreamApiForm));
